// ---------------------------------------------------------------------------
// Модел за реалните разходи при периодично инвестиране през брокер.
//
// Обхваща само разходи, които са потвърдени в централния registry
// (src/data/providers/brokers.ts). Ако даден брокер няма потвърдена
// стойност за конкретния сценарий, той просто не участва в сценария -
// не се допускат предположения.
//
// Изрични допускания (показват се и на страницата):
//  - Един ордер на месец (класически DCA).
//  - Валутната такса се начислява само когато валутата на актива е
//    различна от валутата на сметката.
//  - Моделът НЕ включва spread, борсови такси на трети страни, данъци,
//    нито разходите на самия фонд (TER).
// ---------------------------------------------------------------------------

/** Валутата, в която е деноминиран активът. */
export type AssetCurrency = "EUR" | "USD";

export interface BrokerCostProfile {
  slug: string;
  name: string;
  /** Валута на сметката, която BG клиент може да поддържа. */
  accountCurrency: AssetCurrency;
  /** Комисиона като процент от стойността на ордера (0.05 = 0.05%). */
  commissionPct: number;
  /** Фиксирана комисиона на ордер в EUR. */
  commissionFixedEur: number;
  /** Минимална комисиона на ордер в EUR (0 = няма минимум). */
  commissionMinEur: number;
  /** Валутна конверсия като процент (0.15 = 0.15%). */
  fxFeePct: number;
  /** Минимална такса за валутна конверсия в EUR на сделка (0 = няма). */
  fxMinEur?: number;
  /** Такса за неактивност в EUR на месец (0 = няма). */
  inactivityEurPerMonth: number;
  /** Сценарии, за които профилът има потвърдени данни. */
  supports: AssetCurrency[];
  sourceUrl: string;
  verifiedAt: string;
  verifiedBy: string;
  notes?: string;
}

export interface CostInput {
  profile: BrokerCostProfile;
  /** Инвестирана сума на месец в EUR. */
  monthlyEur: number;
  /** Валута на актива, който се купува. */
  assetCurrency: AssetCurrency;
  /** Брой месеци (12 = една година). */
  months: number;
}

export interface CostResult {
  invested: number;
  commission: number;
  fx: number;
  inactivity: number;
  total: number;
  /** Разходите като процент от инвестираната сума. */
  costPct: number;
  /** Реално вложената в актива сума след разходи. */
  netInvested: number;
}

export function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** Разход за един ордер (комисиона + валутна конверсия) в EUR. */
export function orderCost(
  profile: BrokerCostProfile,
  amountEur: number,
  assetCurrency: AssetCurrency
): { commission: number; fx: number } {
  const pctPart = (amountEur * profile.commissionPct) / 100;
  const raw = pctPart + profile.commissionFixedEur;
  const commission = Math.max(raw, profile.commissionMinEur);
  const fx =
    assetCurrency === profile.accountCurrency
      ? 0
      : Math.max((amountEur * profile.fxFeePct) / 100, profile.fxMinEur ?? 0);
  return { commission, fx };
}

export function computeCost({
  profile,
  monthlyEur,
  assetCurrency,
  months,
}: CostInput): CostResult {
  const per = orderCost(profile, monthlyEur, assetCurrency);
  const commission = round2(per.commission * months);
  const fx = round2(per.fx * months);
  const inactivity = round2(profile.inactivityEurPerMonth * months);
  const invested = round2(monthlyEur * months);
  const total = round2(commission + fx + inactivity);
  return {
    invested,
    commission,
    fx,
    inactivity,
    total,
    costPct: invested > 0 ? round2((total / invested) * 100) : 0,
    netInvested: round2(invested - total),
  };
}

/** Само профилите с потвърдени данни за дадената валута на актива. */
export function eligibleProfiles(
  profiles: BrokerCostProfile[],
  assetCurrency: AssetCurrency
): BrokerCostProfile[] {
  return profiles.filter((p) => p.supports.includes(assetCurrency));
}

/** Класация по общ разход за периода (възходящо). */
export function rankByCost(
  profiles: BrokerCostProfile[],
  monthlyEur: number,
  assetCurrency: AssetCurrency,
  months: number
): { profile: BrokerCostProfile; result: CostResult }[] {
  return eligibleProfiles(profiles, assetCurrency)
    .map((profile) => ({
      profile,
      result: computeCost({ profile, monthlyEur, assetCurrency, months }),
    }))
    .sort((a, b) => a.result.total - b.result.total);
}
